<template>
  <div class="w-full max-w-md mx-auto px-4 py-6 space-y-6">
    <!-- Contract Status Card -->
    <div class="bg-white rounded-lg shadow-sm border border-gray-200 mt-4">
      <div class="bg-gradient-to-r from-ccm-blue-100 to-ccm-blue-200 px-4 py-3 rounded-t-lg">
        <div class="flex items-center justify-between">
          <h2 class="font-semibold text-white text-lg">
            {{ $t('Mobile.HeadContract.MobileHeadContractDashboard.contractStatus') }}
          </h2>

          <div class="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-5 h-5 text-white">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>
          </div>
        </div>
      </div>

      <div class="p-4">
        <Transition
          enter-active-class="animate__animated animate__fadeIn animate__fast"
          mode="out-in"
        >
          <div
            v-if="project"
            class="space-y-4"
          >
            <div class="grid grid-cols-2 gap-4 text-sm">
              <div class="bg-gray-50 rounded-lg p-3">
                <div class="text-gray-600 font-medium">
                  {{ $t('Mobile.HeadContract.MobileHeadContractDashboard.startDate') }}
                </div>

                <div class="font-semibold text-gray-900">
                  {{ project.contract_start_date ? project.contractStartDate.toLocaleDateString() : $t('Mobile.HeadContract.MobileHeadContractDashboard.notSet') }}
                </div>
              </div>

              <div class="bg-gray-50 rounded-lg p-3">
                <div class="text-gray-600 font-medium">
                  {{ $t('Mobile.HeadContract.MobileHeadContractDashboard.endDate') }}
                </div>

                <div class="font-semibold text-gray-900">
                  {{ project.contract_end_date ? project.contractEndDate.toLocaleDateString() : $t('Mobile.HeadContract.MobileHeadContractDashboard.notSet') }}
                </div>
              </div>

              <div class="bg-gray-50 rounded-lg p-3">
                <div class="text-gray-600 font-medium">
                  {{ $t('Mobile.HeadContract.MobileHeadContractDashboard.duration') }}
                </div>

                <div class="font-semibold text-gray-900">
                  {{ project.contract_start_date && project.contract_end_date ? `${project.contractLengthInWeeks} ${$t('Mobile.HeadContract.MobileHeadContractDashboard.weeks')}` : $t('Mobile.HeadContract.MobileHeadContractDashboard.notSet') }}
                </div>
              </div>

              <div class="bg-gray-50 rounded-lg p-3">
                <div class="text-gray-600 font-medium">
                  {{ $t('Mobile.HeadContract.MobileHeadContractDashboard.contractValue') }}
                </div>

                <div class="font-semibold text-blue-600">
                  {{ australianCurrency(totalBudget) }}
                </div>
              </div>
            </div>

            <div class="space-y-3">
              <div class="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                <div class="flex items-center space-x-2">
                  <span class="text-sm font-medium text-blue-600">{{ $t('Mobile.HeadContract.MobileHeadContractDashboard.approvedVariations') }}</span>

                  <!-- <span class="h-6 w-6 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">{{ approvedVariationOrders.length }}</span> -->
                  <span class="ml-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">{{ approvedVariationOrders.length }}</span>
                </div>

                <div class="font-semibold text-blue-600">
                  {{ australianCurrency(variationOrdersTotalApproved) }}
                </div>
              </div>

              <div class="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                <span class="text-sm font-medium text-red-600">{{ $t('Mobile.HeadContract.MobileHeadContractDashboard.finalAccount') }}</span>

                <div class="font-semibold text-red-600">
                  {{ australianCurrency(totalBudget + variationOrdersTotalApproved) }}
                </div>
              </div>

              <div class="flex justify-between items-center p-3 bg-orange-50 rounded-lg">
                <span class="text-sm font-medium text-orange-600">{{ $t('Mobile.HeadContract.MobileHeadContractDashboard.paymentsCertified') }}</span>

                <div class="font-semibold text-orange-600">
                  {{ australianCurrency(claimCertifiedTotal) }}
                </div>
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </div>

    <!-- Work Breakdown Card -->
    <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div class="bg-gradient-to-r from-ccm-blue-100 to-ccm-blue-200 px-4 py-3">
        <div class="flex items-center justify-between">
          <h2 class="font-semibold text-white text-lg">
            {{ $t('Mobile.HeadContract.MobileHeadContractDashboard.projectProgress') }}
          </h2>

          <div class="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" class="w-5 h-5 text-white">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
</svg>
          </div>
        </div>
      </div>

      <div class="p-4">
        <div class="flex items-center justify-between mb-4">
          <div class="flex items-center space-x-4">
            <div class="flex items-center space-x-2">
              <div class="w-3 h-3 bg-blue-500 rounded-full" />

              <span class="text-sm text-gray-600 font-medium">{{ $t('Mobile.HeadContract.MobileHeadContractDashboard.workDone') }}</span>
            </div>

            <div class="flex items-center space-x-2">
              <div class="w-3 h-3 bg-red-500 rounded-full" />

              <span class="text-sm text-gray-600 font-medium">{{ $t('Mobile.HeadContract.MobileHeadContractDashboard.remaining') }}</span>
            </div>
          </div>

          <div class="flex flex-col items-center">
            <div class="font-bold text-gray-900">
              {{ australianCurrency(claimCertifiedTotal) }}
            </div>

            <div class="text-xs text-gray-500">
              {{ $t('Mobile.HeadContract.MobileHeadContractDashboard.of') }} {{ australianCurrency(totalBudget + variationOrdersTotalApproved) }}
            </div>
          </div>
        </div>


        <div class="flex justify-center mb-4">
          <div class="relative w-32 h-32 flex items-center justify-center">
            <CompletionDonutChart
              :claim-certified-total="claimCertifiedTotal"
              :remaining-budget="remainingBudget"
              :total-budget="totalBudget"
              :variation-orders-total-approved="variationOrdersTotalApproved"
            />
          </div>
        </div>

        <!-- Progress Stats -->
        <div class="grid grid-cols-2 gap-3">
          <div class="bg-blue-50 rounded-lg p-3 text-center">
            <div class="text-sm text-blue-600 font-medium">
              {{ $t('Mobile.HeadContract.MobileHeadContractDashboard.spent') }}
            </div>

            <div class="text-xl font-bold text-blue-900">
              {{ australianCurrency(claimCertifiedTotal) }}
            </div>
          </div>

          <div class="bg-red-50 rounded-lg p-3 text-center">
            <div class="text-sm text-red-600 font-medium">
              {{ $t('Mobile.HeadContract.MobileHeadContractDashboard.remaining') }}
            </div>

            <div class="text-xl font-bold text-red-900">
              {{ australianCurrency(remainingBudget) }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import CompletionDonutChart from '@/components/HeadContract/Dashboard/CompletionDonutChart.vue';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { australianCurrency, cumulativeSum, roundDecimals } from '@/helpers';
import { VariationOrderStatus } from '@/models/VariationOrder';
import { useClaimsStore } from '@/store/claims';
import { useNavStore } from '@/store/nav';
import { useProjectsStore } from '@/store/projects';
import { useTradesStore } from '@/store/trades';
import { useUserStore } from '@/store/user';
import { useVariationOrdersStore } from '@/store/variationOrders';
import { DateTime } from 'luxon';
import { computed, watch } from 'vue';

const props = defineProps<{
  projectId: string;
}>();

const navStore = useNavStore();

navStore.backRoute = { name: 'MobileProjectPage', params: { projectId: props.projectId } };

const projectsStore = useProjectsStore();

const project = computed(() => {
  return projectsStore.models.find(parseInt(props.projectId));
});

const claimsStore = useClaimsStore();
const tradesStore = useTradesStore();
const variationOrdersStore = useVariationOrdersStore();
const fetchClaimsAction = useStoreApiAction(claimsStore.fetchClaims);
const fetchTradesAction = useStoreApiAction(tradesStore.fetchTrades);
const fetchVariationsAction = useStoreApiAction(variationOrdersStore.fetchVariationOrders);

watch(() => project.value?.id, (newValue) => {
  if(newValue) {
    fetchTradesAction.request(newValue, { boq_type: 'direct' }).catch((error) => {
      console.log(error);
    });

    fetchClaimsAction.request(newValue, { boq_type: 'direct' }).catch((error) => {
      console.log(error);
    });

    fetchVariationsAction.request(newValue).catch((error) => {
      console.log(error);
    });
  }
}, { immediate: true });

const claims = computed(() => {
  return claimsStore.models.where('project_id', project.value?.id).where('boq_type', 'direct').get();
});

const trades = computed(() => {
  return tradesStore.models.where('project_id', project.value?.id).where('boq_type', 'direct').get();
});

const claimCertifiedTotal = computed(() => {
  return claims.value.reduce((total, claim) => {
    if(claim.totalCertified) {
      total += claim.totalCertified;
    }

    return total;
  }, 0);
});

const totalBudget = computed(() => {
  return trades.value.reduce((total, current) => {
    return total + current.totalBudget;
  }, 0);
});

const remainingBudget = computed(() => {
  return Math.max(totalBudget.value + variationOrdersTotalApproved.value - claimCertifiedTotal.value, 0);
});

const approvedVariationOrders = computed(() => {
  return variationOrdersStore.models.where('project_id', project.value?.id).where(
    'status',
    VariationOrderStatus.approved,
  ).get();
});

const variationOrdersTotalApproved = computed(() => {
  return approvedVariationOrders.value
    .reduce((total, variationOrder) => {
      if(variationOrder.budget) {
        return total + variationOrder.budget;
      }

      return total;
    }, 0);
});
</script>

<style scoped></style>
